import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { usePrefersReducedMotion } from './usePrefersReducedMotion';

type Options = {
  count: number;
  /** Where the feed opens. Read once: the component mounts fresh on each open. */
  initialIndex: number;
  /** Reported up so the owner can reopen where the visitor left off. */
  onActiveChange?: ((index: number) => void) | undefined;
};

export type ReelFeed = {
  scrollerRef: RefObject<HTMLDivElement | null>;
  /** The slide filling the frame. Only this one plays. */
  active: number;
  goTo: (index: number) => void;
  next: () => void;
  previous: () => void;
  /**
   * Whether slide `index` may attach its video source. True for the active
   * slide and its two neighbours, and false for everything else.
   */
  shouldLoad: (index: number) => boolean;
  /** Attach to the scroller. Arrow, Page, Home and End keys. */
  onKeyDown: (event: KeyboardEvent) => void;
};

/**
 * The vertical feed: which slide is live, and how a visitor gets between them.
 *
 * ## The scrolling is the browser's
 *
 * Paging is native CSS scroll-snap on a real overflow container, not a
 * transform driven by pointer events. That is the only way the feed tracks a
 * thumb at 120Hz, keeps its momentum, and behaves on a trackpad, a mouse
 * wheel and a screen reader's own scrolling without any of it being written
 * here. `scroll-snap-type: y mandatory` is what stops it resting between two
 * clips.
 *
 * ## The active slide is read from the position, not from an observer
 *
 * `round(scrollTop / height)` — so the live slide is whichever one is more
 * than half way into the frame, exactly, every time. An `IntersectionObserver`
 * is the usual answer here and it is the wrong one: its callback carries only
 * the entries that changed, so a fast flick past three clips can hand you a
 * slide that is already gone and the feed plays the wrong video. Reading the
 * absolute scroll position cannot pick the wrong slide, because it is not
 * looking at a diff.
 *
 * The read is throttled onto a frame, so a flick does arithmetic once per
 * paint rather than once per scroll event.
 *
 * ## Only three slides hold a source
 *
 * The active clip plus one either side. A feed that attaches forty `<video>`
 * elements starts forty connections on a Ugandan mobile connection; a feed
 * that attaches only the active one stalls on every swipe while the next clip
 * negotiates from scratch. Three is the window that makes a swipe instant and
 * costs one clip of headroom in each direction.
 */
export function useReelFeed({ count, initialIndex, onActiveChange }: Options): ReelFeed {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Read once. The feed is only mounted while it is open, so "the index this
  // opened at" and "the index at mount" are the same thing — and keeping it in
  // a ref is what stops the jump below from re-firing as the visitor scrolls.
  const openedAt = useRef(Math.min(Math.max(initialIndex, 0), Math.max(count - 1, 0)));
  const [active, setActive] = useState(openedAt.current);

  /**
   * Jump to the opening slide before the first paint.
   *
   * A layout effect, not an effect: with `useEffect` the feed paints at slide
   * zero and then jumps, which on a tap into the fourth clip is a visible
   * flash of somebody else's car. `scrollTop` is assigned directly rather than
   * scrolled smoothly — this is not navigation, it is where the feed starts.
   */
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || count < 1) return;
    scroller.scrollTop = openedAt.current * scroller.clientHeight;
  }, [count]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const read = () => {
      frame.current = null;
      const height = scroller.clientHeight;
      if (height < 1) return;
      const next = Math.min(Math.max(Math.round(scroller.scrollTop / height), 0), count - 1);
      setActive((current) => (current === next ? current : next));
    };

    const onScroll = () => {
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(read);
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [count]);

  // Reported on a separate pass so the owner's state update cannot re-enter
  // the scroll handler that caused it.
  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  const goTo = useCallback(
    (index: number) => {
      const scroller = scrollerRef.current;
      if (!scroller || count < 1) return;
      const target = Math.min(Math.max(index, 0), count - 1);
      scroller.scrollTo({
        top: target * scroller.clientHeight,
        // Smooth scrolling is motion the visitor did not ask for, and over a
        // full-screen video it is a lot of it.
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    },
    [count, reducedMotion],
  );

  const next = useCallback(() => goTo(active + 1), [goTo, active]);
  const previous = useCallback(() => goTo(active - 1), [goTo, active]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
        case 'PageDown':
          event.preventDefault();
          next();
          break;
        case 'ArrowUp':
        case 'PageUp':
          event.preventDefault();
          previous();
          break;
        case 'Home':
          event.preventDefault();
          goTo(0);
          break;
        case 'End':
          event.preventDefault();
          goTo(count - 1);
          break;
        default:
          break;
      }
    },
    [next, previous, goTo, count],
  );

  /**
   * The paging keys, bound to the whole dialog rather than to the scroller.
   *
   * Same trap as the shell's own handler: MUI traps focus on a container above
   * this element, so a handler on the scroller does not see a key until
   * something inside the scroller has been clicked. Binding to the enclosing
   * dialog catches them from the moment it opens. It is scoped to that dialog
   * and not to the document, so nothing is intercepted once the viewer closes
   * — and while it is open the dialog is modal anyway, so there is nothing
   * else the keys could have been meant for.
   */
  useEffect(() => {
    const scroller = scrollerRef.current;
    const target = scroller?.closest('[role="dialog"]') ?? scroller;
    if (!target) return;

    const handler = (event: Event) => onKeyDown(event as unknown as KeyboardEvent);
    target.addEventListener('keydown', handler);
    return () => target.removeEventListener('keydown', handler);
  }, [onKeyDown]);

  const shouldLoad = useCallback((index: number) => Math.abs(index - active) <= 1, [active]);

  return { scrollerRef, active, goTo, next, previous, shouldLoad, onKeyDown };
}
